'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { Modal } from '@/Modal'
import type { PerformerScheduleAppearance } from '../the-session/performerSchedule'

export const CompactPerformerCard = ({
  name,
  image,
  alt,
  description,
  eventName,
  scheduleAppearances = [],
  scheduleHref,
}: CompactPerformerCardProps) => {
  const [isBioOpen, setIsBioOpen] = useState(false)

  return (
    <>
      <article className="flex items-stretch overflow-hidden rounded-md border border-white/10 bg-white">
        {image ? (
          <div className="relative w-28 shrink-0 self-stretch overflow-hidden bg-gray-200 sm:w-32">
            <Image
              src={`${image}?w=128&dpr=2`}
              alt={alt || name}
              fill
              sizes="(max-width: 639px) 112px, 128px"
              unoptimized
              className="object-cover object-top"
            />
          </div>
        ) : (
          <div className="flex w-28 shrink-0 items-center justify-center bg-gray-950 p-4 text-center text-white sm:w-32">
            <span className="text-xl font-black leading-tight">{name}</span>
          </div>
        )}

        <div className="flex min-w-0 flex-grow flex-col p-3 sm:p-4">
          <h3 className="mb-2 text-lg font-bold">{name}</h3>
          <p className="line-clamp-3 text-sm leading-relaxed text-gray-700">
            {description}
          </p>
          <button
            type="button"
            onClick={() => setIsBioOpen(true)}
            className="mt-auto inline-flex w-fit cursor-pointer items-center gap-1 pt-3 text-sm font-bold text-vi-red transition-colors duration-200 hover:text-red-700 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-vi-red"
            aria-haspopup="dialog"
            aria-label={`Read full biography for ${name}`}
          >
            Read full bio
            <svg
              aria-hidden="true"
              className="size-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </article>

      <Modal
        isOpen={isBioOpen}
        onClose={() => setIsBioOpen(false)}
        maxWidth="3xl"
        backdropOpacity="dark"
        className="mx-2 sm:mx-4"
        ariaLabel={`${name} biography`}
      >
        <div className="grid max-h-[85vh] overflow-y-auto md:grid-cols-[minmax(240px,0.85fr)_1.15fr]">
          {image ? (
            <div className="relative min-h-64 overflow-hidden bg-gray-200 md:min-h-[430px]">
              <Image
                src={`${image}?w=480&dpr=2`}
                alt={alt || name}
                fill
                sizes="(max-width: 767px) 100vw, 320px"
                unoptimized
                className="object-cover object-top"
              />
            </div>
          ) : (
            <div className="flex min-h-64 items-center justify-center bg-gray-950 p-8 text-center text-white md:min-h-[430px]">
              <span className="text-4xl font-black leading-tight">{name}</span>
            </div>
          )}

          <div className="flex flex-col justify-center p-6 sm:p-8 md:p-10">
            {eventName && (
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-vi-red">
                {eventName}
              </p>
            )}
            <h3 className="mb-5 text-3xl font-black leading-tight text-gray-950 sm:text-4xl">
              {name}
            </h3>
            <p className="text-base leading-relaxed text-gray-700 sm:text-lg">
              {description}
            </p>

            {scheduleHref && scheduleAppearances.length > 0 && (
              <div className="mt-7 border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon
                    aria-hidden="true"
                    className="size-5 text-vi-red"
                  />
                  <h4 className="text-base font-bold text-gray-950">
                    On the schedule
                  </h4>
                </div>

                <ul className="mt-3 space-y-2">
                  {scheduleAppearances.map(appearance => (
                    <li
                      key={appearance.id}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                    >
                      <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
                        <time
                          dateTime={appearance.date}
                          className="font-bold text-gray-950"
                        >
                          {appearance.dateLabel}
                        </time>
                        <span aria-hidden="true">·</span>
                        <span>{appearance.time}</span>
                      </div>
                      <p className="mt-1 font-semibold text-gray-950">
                        {appearance.title}
                      </p>
                      {appearance.room && (
                        <p className="mt-1 text-sm text-gray-600">
                          {appearance.room}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>

                <Link
                  href={scheduleHref}
                  onClick={() => setIsBioOpen(false)}
                  className="mt-3 inline-flex w-fit items-center gap-1 text-sm font-bold text-vi-red transition-colors duration-200 hover:text-red-700 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-vi-red"
                >
                  View the full schedule
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

interface CompactPerformerCardProps {
  name: string
  image?: string
  alt?: string
  description: string
  eventName?: string
  scheduleAppearances?: PerformerScheduleAppearance[]
  scheduleHref?: string
}
